import { useCallback, useEffect, useState } from "react";

import * as api from "../engine/api";

/**
 * AccountClosure — the emailed-link landing for A13 account closure.
 *
 * The Console has NO router; App.jsx routes here when the browser lands on
 * `/account-closure?token=<raw>&action=confirm|cancel` (see hooks/useAuth.js).
 *
 * **It does not act on mount, and that is the whole safety property of this screen.**
 * `ResetPassword.jsx` already carries this reasoning for F14 — a link preloader, a mail
 * scanner or a chat-app unfurler fetches URLs unasked — but the stakes differ by a lot. There
 * the cost of a mutating arrival was a burnt single-use token the customer could request
 * again. Here it would be a CLOSED ACCOUNT. So the only thing mount does is a GET that the
 * server guarantees changes nothing, and nothing is sent until a button is pressed.
 *
 * `action` from the link PRESELECTS a button and never performs it. A scanner that follows
 * `&action=confirm` gets a rendered page and no request.
 *
 * Confidentiality: the raw token arrives as a prop, is held in memory for the one POST, and is
 * never written to localStorage, never rendered, and never interpolated into an error message.
 * The address is masked by the server before it reaches this component (the endpoint takes no
 * session, so a leaked token must not become an address lookup).
 *
 * Color semantics (.impeccable.md): danger(red) for the confirm action, because it is the
 * irreversible one; accent for the interactive cancel; ok(green) only for a completed outcome.
 */

/**
 * The link states no retry will fix, keyed off the stable OMEGA-590 `code` rather than the
 * human message, which is copy and may change. Returning null means "not a dead link".
 */
function deadLinkMessage(err) {
  if (err?.code === "not_found") {
    return "This link is not valid, or it has expired. Closure links last three days; ask us for a new one if you still want to close your account.";
  }
  if (err?.code === "already_resolved") {
    return "This request has already been answered, so this link no longer does anything.";
  }
  return null;
}

export default function AccountClosure({ token, action = "", onLeave }) {
  const [request, setRequest] = useState(null);
  const [deadLink, setDeadLink] = useState("");
  const [loadError, setLoadError] = useState("");
  const [outcome, setOutcome] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [busy, setBusy] = useState(false);

  // The ONLY thing mount does. A read, by a server that guarantees it changes nothing, so a
  // preloader fetching this page produces a render and no state change anywhere.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.readClosureRequest(token);
        if (alive) setRequest(data);
      } catch (err) {
        if (!alive) return;
        const dead = deadLinkMessage(err);
        if (dead) setDeadLink(dead);
        else setLoadError("We could not load this request. Try the link again in a moment.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = useCallback(
    async (kind) => {
      setBusy(true);
      setSubmitError("");
      try {
        if (kind === "confirm") await api.confirmClosure(token);
        else await api.cancelClosure(token);
        setOutcome(kind);
      } catch (err) {
        const dead = deadLinkMessage(err);
        if (dead) setDeadLink(dead);
        else setSubmitError("That did not go through. Try again, or write to us if it keeps failing.");
      } finally {
        setBusy(false);
      }
    },
    [token],
  );

  const card = "border-3 border-line shadow-brutal bg-surface p-8 max-w-xl w-full";
  const heading = "font-display text-2xl text-fg";

  if (deadLink) {
    return (
      <main className="min-h-screen bg-canvas flex items-center justify-center p-8">
        <div className={card}>
          <h1 className={heading}>Account closure</h1>
          <p className="font-sans text-fg2 mt-4">{deadLink}</p>
          <button
            type="button"
            onClick={onLeave}
            className="mt-8 border-3 border-line shadow-brutal rounded-none bg-accent-fill text-surface font-sans font-bold px-4 py-2 cursor-pointer"
          >
            Back to sign in
          </button>
        </div>
      </main>
    );
  }

  if (outcome) {
    return (
      <main className="min-h-screen bg-canvas flex items-center justify-center p-8">
        <div className={card}>
          <h1 className={heading}>{outcome === "confirm" ? "Your account is closed" : "Closure cancelled"}</h1>
          <p className="font-sans text-ok mt-4">
            {outcome === "confirm"
              ? "You have been signed out everywhere. Your invoices are kept for our legal retention period."
              : "Nothing has changed. Your account is still open and you can keep using it."}
          </p>
          <button
            type="button"
            onClick={onLeave}
            className="mt-8 border-3 border-line shadow-brutal rounded-none bg-accent-fill text-surface font-sans font-bold px-4 py-2 cursor-pointer"
          >
            Back to sign in
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-canvas flex items-center justify-center p-8">
      <div className={card}>
        <h1 className={heading}>Close your account?</h1>

        {loadError && <p className="font-sans text-danger mt-4">{loadError}</p>}

        {request && (
          <>
            <p className="font-sans text-fg2 mt-4">
              Our support team started this on your behalf. Nothing happens until you choose below.
            </p>
            <dl className="mt-6 font-mono text-sm text-fg2">
              <div className="flex justify-between border-b border-line py-2">
                <dt>Account</dt>
                <dd className="text-fg">{request.account_masked}</dd>
              </div>
              <div className="flex justify-between py-2">
                <dt>This link expires</dt>
                <dd className="text-fg">{new Date(request.expires_at).toLocaleString()}</dd>
              </div>
            </dl>
            <p className="font-sans text-fg2 mt-6">
              Closing signs you out everywhere and stops you using the platform. Your invoices are kept for our
              legal retention period.
            </p>

            {submitError && <p className="font-sans text-danger mt-4">{submitError}</p>}

            <div className="mt-8 flex gap-4">
              <button
                type="button"
                disabled={busy}
                onClick={() => submit("confirm")}
                className={`border-3 border-danger rounded-none bg-danger-tint text-danger font-sans font-bold px-4 py-2 cursor-pointer disabled:opacity-50 ${
                  action === "confirm" ? "shadow-brutal" : "shadow-brutal-sm"
                }`}
              >
                Yes, close my account
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => submit("cancel")}
                className={`border-3 border-line rounded-none bg-accent-fill text-surface font-sans font-bold px-4 py-2 cursor-pointer disabled:opacity-50 ${
                  action === "cancel" ? "shadow-brutal" : "shadow-brutal-sm"
                }`}
              >
                No, keep my account
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
